/**
 * useRevokeAgentToken Hook
 *
 * React Query mutation hook for revoking an agent token.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dataAgentService } from '../../../api/data-agent.service';
import { DATA_AGENTS_QUERY_KEY } from './useDataAgents';
import { DATA_AGENT_DETAILS_QUERY_KEY } from './useDataAgentDetails';
import type { DataAgent } from '../../../types/data-agent.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseRevokeAgentTokenOptions {
  onSuccess?: (dataAgent: DataAgent) => void;
  onError?: (error: string) => void;
}

export function useRevokeAgentToken(
  tenantId: string,
  dataAgentId: string,
  options?: UseRevokeAgentTokenOptions
) {
  const queryClient = useQueryClient();

  return useMutation<DataAgent, Error, void>({
    mutationFn: () => dataAgentService.revokeToken(tenantId, dataAgentId),
    onSuccess: (dataAgent) => {
      queryClient.invalidateQueries({ queryKey: [DATA_AGENTS_QUERY_KEY, tenantId] });
      queryClient.invalidateQueries({
        queryKey: [DATA_AGENT_DETAILS_QUERY_KEY, tenantId, dataAgentId],
      });
      options?.onSuccess?.(dataAgent);
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
}
